import { useRef, useState, useEffect, useCallback, Suspense } from "react";
import { Canvas } from '@react-three/fiber';
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "./authSlice";
import { useLoginMutation } from "./authApiSlice";
import { signInWithPopup, signOut } from "firebase/auth";
import { auth, googleProvider, appleProvider } from "../../firebase";
import { baseUrl } from "../../app/api/apiSlice";
import { useLazyValidateInviteTokenQuery } from "../contractors/contractorsApiSlice";
import { rememberInviteToken, rememberedInviteToken } from "./inviteToken";
import "./Login.css";

import { IntroScene } from './new_login_components/IntroScene';
import { LoginPanel } from './new_login_components/LoginPanel';

export const inviteTokenFromLocation = (location) => {
  const fromSearch = location?.state?.from?.search || '';
  const ownSearch = location?.search || '';
  return (
    new URLSearchParams(fromSearch).get('token') ||
    new URLSearchParams(ownSearch).get('token') ||
    rememberedInviteToken() ||  // back on a bare /login after a logout (#199)
    null
  );
};

const getPostLoginPath = (userData) => {
  if (!userData?.onboarding_completed) return '/onboarding';
  if (!userData?.is_approved) return '/pending-approval';
  return '/landing';
};

export const postLoginDestination = (user, inviteToken) => {
  const dest = getPostLoginPath(user);
  if (dest === '/onboarding' && inviteToken) {
    return `/onboarding?token=${encodeURIComponent(inviteToken)}`;
  }
  return dest;
};

// Easing function for smooth transitions
const easeInOutCubic = (t) => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};
// Exact cubic-bezier(0.4, 0, 0.2, 1) for slide animations
const cubicBezier = (t, p1x, p1y, p2x, p2y) => {
  let x = t;
  for (let i = 0; i < 8; i++) {
    const currentX = 3 * p1x * Math.pow(1 - x, 2) * x + 3 * p2x * (1 - x) * x * x + x * x * x;
    const currentSlope = 3 * p1x * Math.pow(1 - x, 2) - 6 * p1x * (1 - x) * x + 3 * p2x * 2 * (1 - x) * x - 3 * p2x * x * x + 3 * x * x;
    if (Math.abs(currentX - t) < 0.0001) break;
    x -= (currentX - t) / currentSlope;
  }
  return 3 * p1y * Math.pow(1 - x, 2) * x + 3 * p2y * (1 - x) * x * x + x * x * x;
};
const easeOutSlide = (t) => cubicBezier(t, 0.4, 0, 0.2, 1);

// ponytail: sessionStorage, not redux — the intro is per-tab chrome and this component is
// its only consumer. Forgetting on tab close is the behaviour we want, not a limitation.
// Storage access throws outright in a sandboxed iframe or a managed browser with site data
// blocked. /login is the one route that must never white-screen, and replaying the intro is a
// far cheaper failure than not rendering the sign-in panel at all.
const INTRO_SEEN_KEY = 'ace:intro-seen';
const introAlreadySeen = () => {
  try {
    return sessionStorage.getItem(INTRO_SEEN_KEY) === '1';
  } catch {
    return false;
  }
};
const markIntroSeen = () => {
  try {
    sessionStorage.setItem(INTRO_SEEN_KEY, '1');
  } catch {
    // storage blocked — the intro replays next mount, which is acceptable
  }
};

// Stage durations in milliseconds (48% of original)
const STAGE_DURATIONS = [
  384,   // 0: Black screen
  576,   // 1: Logo fade in
  720,   // 2: Logo hold
  576,   // 3: Transition to intro-3
  720,   // 4: intro-3 hold
  720,   // 5: Transition to intro-4
  720,   // 6: intro-4 hold
  384,   // 7: Slide for login
  0,     // 8: Final state (infinite)
];


const Login = () => {
  const userRef = useRef();
  const [username, setUser] = useState("");
  const [password, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const hasToken = useSelector((state) => state.auth.token);

  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();

  const inviteToken = inviteTokenFromLocation(location);
  const [validateToken] = useLazyValidateInviteTokenQuery();
  const [invite, setInvite] = useState(null);
  useEffect(() => {
    if (!inviteToken) return;
    rememberInviteToken(inviteToken);
    validateToken(inviteToken).unwrap().then(setInvite).catch(() => setInvite({ error: true }));
  }, [inviteToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animation state
  const [currentStage, setCurrentStage] = useState(introAlreadySeen() ? 8 : 0);
  const [stageProgress, setStageProgress] = useState(0);
  const [stageStartTime, setStageStartTime] = useState(Date.now());
  const [showLogin, setShowLogin] = useState(introAlreadySeen());
  const [loginSlideProgress, setLoginSlideProgress] = useState(0);
  const [bgSlideProgress, setBgSlideProgress] = useState(0);

  
  useEffect(() => {
    let animationId;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - stageStartTime;
      const duration = STAGE_DURATIONS[currentStage];

      if (duration > 0) {
        const linearProgress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutCubic(linearProgress);
        setStageProgress(easedProgress);

        // Calculate slide progress for stage 7 (login slide)
        if (currentStage === 7 || currentStage === 8) {
          // Login panel: starts immediately, 840ms duration (70% of 1200)
          const loginElapsed = currentStage === 8 ? 840 : elapsed;
          const loginLinear = Math.min(1, loginElapsed / 840);
          setLoginSlideProgress(easeOutSlide(loginLinear));

          // Background: starts 210ms after, 630ms duration (70% of original)
          const bgDelay = 210;
          const bgDuration = 630;
          const bgElapsed = currentStage === 8 ? 630 : Math.max(0, elapsed - bgDelay);
          const bgLinear = Math.min(1, bgElapsed / bgDuration);
          setBgSlideProgress(easeOutSlide(bgLinear));
        }

        if (linearProgress >= 1 && currentStage < STAGE_DURATIONS.length - 1) {
          setCurrentStage((prev) => prev + 1);
          setStageStartTime(now);
          setStageProgress(0);

          if (currentStage === 6) {
            markIntroSeen();
            setShowLogin(true);
          }
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [currentStage, stageStartTime]);



  const handleSkip = useCallback(() => {
    if (currentStage < 7) {
      markIntroSeen();
      setCurrentStage(7);
      setStageProgress(0);
      setStageStartTime(Date.now());
      setShowLogin(true);
    }
  }, [currentStage]);

  useEffect(() => {
    setErrMsg("");
  }, [username, password]);

  const navigateAfterLogin = (userObj) => {
    navigate(postLoginDestination(userObj, inviteToken));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const userData = await login({ username, password }).unwrap();
      dispatch(setCredentials({ ...userData, username }));
      setUser("");
      setPwd("");
      navigateAfterLogin(userData.user);
    } catch (err) {
      if (!err?.status && isLoading) {
        setErrMsg("No Server Response");
      } else if (err.status === 401) {
        setErrMsg("User not found. Please check your information and try again.");
      } else {
        setErrMsg("Login Failed");
      }
      if (userRef.current) userRef.current.focus();
    }
  };

  const handleGoogleLogin = async () => {
    try {
      if (auth.currentUser) {
        await signOut(auth);
      }

      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const token = await user.getIdToken(true);
      
      const response = await fetch(`${baseUrl}/api/v1/accounts/users/me/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const backendUser = await response.json();

      dispatch(setCredentials({
        username: backendUser.username,
        partner: backendUser.partner,
        access: token,
        user: backendUser
      }));

      navigateAfterLogin(backendUser);
    } catch (error) {
      console.error("Google Login Error:", error);
      setErrMsg("Google Login Failed");
    }
  };

  const handleAppleLogin = async () => {
    try {
      if (auth.currentUser) {
        await signOut(auth);
      }

      const result = await signInWithPopup(auth, appleProvider);
      const user = result.user;
      const token = await user.getIdToken(true);
      
      const response = await fetch(`${baseUrl}/api/v1/accounts/users/me/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const backendUser = await response.json();

      dispatch(setCredentials({
        username: backendUser.username,
        partner: backendUser.partner,
        access: token,
        user: backendUser
      }));

      navigateAfterLogin(backendUser);
    } catch (error) {
      console.error("Apple Login Error:", error);
      console.error("Error Code:", error.code);
      console.error("Error Message:", error.message);
      setErrMsg(`Apple Login Failed: ${error.message} (${error.code})`);
    }
  };

  return (
    <div className="login-container-new" onClick={handleSkip}>
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        gl={{ antialias: true, alpha: false }}
        style={{ background: '#000000' }}
      >
        <color attach="background" args={['#000000']} />
        <Suspense fallback={null}>
          <IntroScene
            currentStage={currentStage}
            stageProgress={stageProgress}
            bgSlideProgress={bgSlideProgress}
          />
        </Suspense>
      </Canvas>

      <LoginPanel
        visible={showLogin}
        username={username}
        setUsername={setUser}
        password={password}
        setPassword={setPwd}
        handleLogin={handleSubmit}
        handleGoogleLogin={handleGoogleLogin}
        handleAppleLogin={handleAppleLogin}
        isLoading={isLoading}
        errMsg={errMsg}
        userRef={userRef}
        slideProgress={loginSlideProgress}
        invite={invite}
        inviteToken={inviteToken}
      />

      {currentStage < 7 && (
        <div className="skip-hint">
          Click anywhere to skip
        </div>
      )}
    </div>
  );
};

export default Login;
